import { useEffect, useState } from 'react'
import { Eye, X, Info, AlertTriangle } from 'lucide-react'
import { superadminApi } from '@/services/api/superadmin'
import type { TeacherSettingsResponse } from '@/types/teacher-request'
import { TeacherSettingsView } from '@/components/teacher-settings/TeacherSettingsView'

/**
 * Saved-state preview of the teacher settings view for superadmins.
 *
 * Renders the exact same view component as the real teacher page
 * (TeacherSettingsView, read-only), so the preview cannot drift from reality.
 */
export function TeacherPreviewModal({ configType, onClose }: {
  /** Config page the preview was opened from — its teacher tab is pre-selected. */
  configType?: string
  onClose: () => void
}) {
  const [data, setData] = useState<TeacherSettingsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    superadminApi.getTeacherSettingsPreview()
      .then(d => { if (alive) setData(d as TeacherSettingsResponse) })
      .catch(err => { if (alive) setError(err instanceof Error ? err.message : 'Vorschau konnte nicht geladen werden') })
    return () => { alive = false }
  }, [])

  // Pre-select the tab containing subgroups of this config type (modal-local state,
  // tabs stay switchable afterwards)
  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!data || !configType || activeCategory !== undefined) return
    const cat = data.categories.find(c =>
      c.subgroups.some(sg => sg.config_type === configType)
    )
    if (cat) setActiveCategory(cat.name)
  }, [data, configType, activeCategory])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-purple-50">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-purple-900">Vorschau — Lehrer-Ansicht</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-purple-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-2 bg-yellow-50 border-b border-yellow-200">
          <p className="text-sm text-yellow-800 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Gespeicherter Zustand — in der Vorschau ist nichts bearbeitbar.
          </p>
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {error && (
            <div className="p-8 text-center text-red-600">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p>{error}</p>
            </div>
          )}
          {!error && !data && (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          )}
          {!error && data && (
            <TeacherSettingsView
              data={data}
              editable={false}
              overrides={{}}
              activeCategory={activeCategory}
              onCategoryChange={name => setActiveCategory(name)}
            />
          )}

        </div>
      </div>
    </div>
  )
}
